import { Component, HostListener, Input, OnInit, Renderer2, SimpleChanges } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HomeService, Plan, RecurrenceTypes } from 'app/public/home/home.service';
import { features } from 'process';
import { LanguageService, TranslatedLanguage } from '../services/language.service';
import { UserService } from 'app/auth/user.service';
import { AuthService } from 'app/auth/auth.service';
import { element } from 'protractor';
import {UserInfo} from '../../auth/user';
import {StorageKeys} from '../../enums/storage-keys';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-plans',
  templateUrl: './plans.component.html',
  styleUrls: ['./plans.component.scss']
})
export class PlansComponent implements OnInit {

  @Input()
  role: 'PUBLIC_ADMINISTRATOR' | 'SOLUTION_PROVIDER' = 'PUBLIC_ADMINISTRATOR';

  @Input()
  isPrivate: boolean;

  @Input()
  isoOnly: boolean = false;

  @Input()
  hasBillingId: boolean = false;

  public plans: Plan[] = [];
  public allPlans: Plan[] = [];
  public currentLanguage: TranslatedLanguage;

  public recurrenceType: RecurrenceTypes = RecurrenceTypes.MONTHLY;
  planSize: boolean = window.innerWidth < 2080;

  public userInfoPayload: UserInfo;
  private navigationSubscription: Subscription;

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.planSize = window.innerWidth < 2080;
  }

  public responsiveOptions = [
    {
        breakpoint: '1235px',
        numVisible: 2,
        numScroll: 1
    },
    {
        breakpoint: '900px',
        numVisible: 1,
        numScroll: 1
    },
];

  constructor(
    private readonly renderer: Renderer2,
    private readonly service: HomeService,
    private readonly language: LanguageService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly userService: UserService
  ) { }

  ngOnInit(): void {
    this.currentLanguage = this.language.current();
    this.getUserInfo();

    this.navigationSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.getUserInfo());
  }

  ngOnDestroy(): void {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { role } = changes;
    this.getUserInfo();
    if(role.currentValue && role.currentValue !== role.previousValue) {
      this._getPlans(role.currentValue);
    }
  }

  private _getPlans(type): void {
    this.service.getPlansOpen(type).then((response:any) => {
      if (response.status == 200) {
        this.allPlans = response.object;
        this.plans = response.object;
        this._filterPlans();
      }
    });
  }

  private _filterPlans(): void {
    if (this.role === 'SOLUTION_PROVIDER') {
      this.plans = this.allPlans
        .map(plan => ({
          ...plan,
          prices: plan.prices.filter(price => price.recurrenceType === this.recurrenceType)
        }))
        .filter(plan => !this.hasBillingId || (this.hasBillingId && plan.billingId))
        .filter(plan => plan.prices.length > 0)
        .map(plan => {
          if (this.recurrenceType === RecurrenceTypes.ANNUAL) {
            const monthlyValue = plan.prices[0].value / 12;
            return {
              ...plan,
              monthlyValue: monthlyValue
            }
          }

          return plan;
        });
    }

    if (this.role === 'PUBLIC_ADMINISTRATOR') {
      this.plans = this.allPlans
        .filter(item => {
          if (!this.isoOnly) {
            return item;
          }

          if (item.hasIsoCertification) {
            return item;
          }
        })
    }
  }

  redirectContact() {
    this.router.navigate(['/contact-us']);
  }

  public moreTxt(planName: string): string {
    if(this.currentLanguage.code === 'pt-BR') {
      return `Tudo que está incluso no Plano ${planName} além de...`;
    }

    if(this.currentLanguage.code === 'en-US') {
      return `Everything included in the ${planName} plan plus...`;
    }
  }

  public choosePlan(planId: string, recurrenceType: string): void {
    if (this.role !== 'SOLUTION_PROVIDER') {
      this.router.navigate(['/contact-us']);
      return;
    }
  
    if (this.userService.isSolutionProvider()) {
      if (!this.userService.isGuest()) {
        this.router.navigate(['/choose-payment-method'], { queryParams: { planId, recurrenceType } });
      } else {
        this.router.navigate(['/account']);
      }
    } else {
      this.saveUserPlanIdChoiceOnLocalStorage(planId);
      this.router.navigate(['/login']);
    }
  }

  public togglePlan(): void {
    if (this.recurrenceType === RecurrenceTypes.ANNUAL) {
      this.recurrenceType = RecurrenceTypes.MONTHLY;
      this._filterPlans();
      return
    }

    if (this.recurrenceType === RecurrenceTypes.MONTHLY) {
      this.recurrenceType = RecurrenceTypes.ANNUAL;
      this._filterPlans();
      return
    }
  }

  public featureClick(el): void {
    if (el.classList.value.split(' ').includes('full-plan-itens-item-extended-opened')) {
      this.renderer.removeClass(el, 'full-plan-itens-item-extended-opened');
    } else {
      this.renderer.addClass(el, 'full-plan-itens-item-extended-opened');
    }
  }

  public featureIsOpen(el): boolean {
    return el.classList.value.split(' ').includes('full-plan-itens-item-extended-opened');
  }

  getUserInfo(): void {

    this.userService.getUserInfo()
        .then((userPayload: UserInfo) => {
          this.userInfoPayload = userPayload;
        });
  }

  saveUserPlanIdChoiceOnLocalStorage(planId: string): void {
    window.sessionStorage.setItem(StorageKeys.sessionPlanIdUserWantToBuy, planId);
  }
}
